import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import SEO from '../../components/common/SEO';
import { OrbitingAtom, BubblingFlask } from '../../components/common/PharmaAnimations';

/* ─── Design tokens ─────────────────────────────────────── */
const C = {
  navy: '#0a1628',
  blue: '#1d4ed8',
  blueHov: '#1e40af',
  blueLt: '#eff6ff',
  blueMd: '#dbeafe',
  teal: '#0891b2',
  green: '#059669',
  text: '#0f172a',
  sub: '#475569',
  muted: '#94a3b8',
  border: '#e2e8f0',
  bg: '#f8fafc',
  white: '#ffffff',
};

/* ─── Scroll-reveal FadeIn ───────────────────────────────── */
function FadeIn({ children, delay = 0, direction = 'up', threshold = 0.1 }) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  const tx = direction === 'up' ? '0,28px,0' : direction === 'down' ? '0,-28px,0' : direction === 'left' ? '28px,0,0' : '-28px,0,0';
  return (
    <div ref={ref} style={{
      transition: `opacity 0.65s ease ${delay}ms, transform 0.65s ease ${delay}ms`,
      opacity: vis ? 1 : 0,
      transform: vis ? 'translate3d(0,0,0)' : `translate3d(${tx})`,
    }}>{children}</div>
  );
}

/* ─── Animated counter ───────────────────────────────────── */
function CountUp({ target, suffix = '', prefix = '', duration = 1400 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const animated = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !animated.current) {
        animated.current = true;
        const num = parseFloat(target);
        if (isNaN(num)) { setVal(target); return; }
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setVal(Math.floor(eased * num));
          if (p < 1) requestAnimationFrame(tick);
          else setVal(num);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);
  if (isNaN(parseFloat(target))) return <span ref={ref}>{target}</span>;
  return <span ref={ref}>{prefix}{val}{suffix}</span>;
}

/* ─── Section helpers ────────────────────────────────────── */
function SectionLabel({ children, light }) {
  return (
    <div style={{
      display: 'inline-block',
      background: light ? 'rgba(255,255,255,0.12)' : C.blueLt,
      color: light ? '#93c5fd' : C.blue,
      border: light ? '1px solid rgba(255,255,255,0.2)' : 'none',
      fontSize: '0.7rem', fontWeight: 700, padding: '4px 14px',
      borderRadius: 20, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.1em',
    }}>{children}</div>
  );
}

function SectionHeading({ label, title, subtitle, light, center = true }) {
  return (
    <div style={{ textAlign: center ? 'center' : 'left', marginBottom: 52, padding: '0 24px' }}>
      {label && <SectionLabel light={light}>{label}</SectionLabel>}
      <h2 style={{
        fontSize: 'clamp(1.4rem, 4vw, 2.3rem)', fontWeight: 900,
        color: light ? C.white : C.text, marginBottom: 14, letterSpacing: '-0.5px',
        lineHeight: 1.15,
      }}>{title}</h2>
      {subtitle && (
        <p style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)', color: light ? 'rgba(255,255,255,0.72)' : C.sub, maxWidth: 560, margin: center ? '0 auto' : undefined, lineHeight: 1.75 }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

/* ─── Product image ─────────────────────────────────────── */
function ProductImage({ item }) {
  const [failed, setFailed] = React.useState(false);
  const isImp = item?.category === 'impurity';
  const bg1 = isImp ? '#eff6ff' : '#f0fdf4';
  const bg2 = isImp ? '#dbeafe' : '#dcfce7';
  const accent = isImp ? C.blue : C.green;
  const label = isImp ? 'IMP' : 'RS';

  if (item?.image && !failed) {
    return (
      <div style={{ width: '100%', height: 130, background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px 12px 0 0', overflow: 'hidden' }}>
        <img src={`/products/${item.image}`} alt={item.name} onError={() => setFailed(true)}
          style={{ maxHeight: 118, maxWidth: '92%', objectFit: 'contain' }} />
      </div>
    );
  }
  return (
    <div style={{ width: '100%', height: 130, borderRadius: '12px 12px 0 0', background: `linear-gradient(135deg,${bg1},${bg2})`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
      <svg width="140" height="100" viewBox="0 0 140 100" style={{ position: 'absolute', opacity: 0.1 }}>
        <circle cx="30" cy="50" r="14" stroke={accent} strokeWidth="2" fill="none" />
        <circle cx="70" cy="22" r="14" stroke={accent} strokeWidth="2" fill="none" />
        <circle cx="110" cy="50" r="14" stroke={accent} strokeWidth="2" fill="none" />
        <circle cx="70" cy="78" r="14" stroke={accent} strokeWidth="2" fill="none" />
        <line x1="44" y1="45" x2="56" y2="30" stroke={accent} strokeWidth="2" />
        <line x1="84" y1="30" x2="96" y2="45" stroke={accent} strokeWidth="2" />
        <line x1="96" y1="55" x2="84" y2="70" stroke={accent} strokeWidth="2" />
        <line x1="56" y1="70" x2="44" y2="55" stroke={accent} strokeWidth="2" />
      </svg>
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.78rem', margin: '0 auto 6px', boxShadow: `0 4px 14px ${accent}44` }}>{label}</div>
        <div style={{ fontSize: '0.62rem', color: accent, fontWeight: 600 }}>{isImp ? 'Impurity Std' : 'Ref. Standard'}</div>
      </div>
    </div>
  );
}

/* ─── Product card ───────────────────────────────────────── */
function ProductCard({ item, delay = 0 }) {
  const [hov, setHov] = useState(false);
  return (
    <FadeIn delay={delay} direction="up">
      <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        style={{
          background: C.white, borderRadius: 14, border: `1px solid ${hov ? C.blue + '30' : C.border}`,
          overflow: 'hidden', display: 'flex', flexDirection: 'column',
          boxShadow: hov ? '0 14px 36px rgba(29,78,216,0.14)' : '0 2px 10px rgba(0,0,0,0.05)',
          transform: hov ? 'translateY(-4px)' : 'none',
          transition: 'all 0.22s ease', height: '100%',
        }}>
        <ProductImage item={item} />
        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: C.text, lineHeight: 1.4 }}>{item.name}</div>
            <span style={{ flexShrink: 0, fontSize: '0.67rem', fontWeight: 700, padding: '3px 8px', borderRadius: 20, background: item.stock_status === 'in_stock' ? '#d1fae5' : '#fef3c7', color: item.stock_status === 'in_stock' ? '#059669' : '#d97706' }}>
              {item.stock_status === 'in_stock' ? 'In Stock' : 'On Request'}
            </span>
          </div>
          <div style={{ fontSize: '0.74rem', color: C.muted, fontFamily: 'monospace', letterSpacing: '0.03em' }}>{item.catalog_number}</div>
          {item.cas_number && <div style={{ fontSize: '0.74rem', color: C.sub }}>CAS: {item.cas_number}</div>}
          {item.purity && <div style={{ fontSize: '0.74rem', color: C.sub }}>Purity: {item.purity}</div>}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
            <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: C.blueLt, color: C.blue }}>
              {item.category === 'impurity' ? 'Impurity' : 'Ref. Standard'}
            </span>
            <Link to={`/contact?product=${encodeURIComponent(item.catalog_number)}&name=${encodeURIComponent(item.name)}`}
              style={{ fontSize: '0.78rem', color: C.blue, fontWeight: 700, textDecoration: 'none' }}>
              Request Quote →
            </Link>
          </div>
        </div>
      </div>
    </FadeIn>
  );
}

/* ─── Process step ───────────────────────────────────────── */
const PROCESS = [
  { step: '01', icon: '🔍', title: 'Share Your Requirement', desc: 'Tell us what compound you need — catalog number, CAS, or describe your molecule. We handle inquiries via email or contact form.' },
  { step: '02', icon: '🧪', title: 'Technical Review', desc: 'Our scientists evaluate feasibility, purity targets, and analytical strategy. You receive a detailed quotation within 24 hours.' },
  { step: '03', icon: '⚗️', title: 'Synthesis & QC', desc: 'Our team synthesizes and characterizes the compound using HPLC, NMR, LC-MS, and IR. Every batch gets a lot-specific CoA.' },
  { step: '04', icon: '📦', title: 'Delivery', desc: 'Compounds shipped with complete documentation — CoA, SDS, and spectral data. Compliant packaging for pharma and regulatory use.' },
];

function ProcessStep({ step, icon, title, desc, delay }) {
  const [hov, setHov] = useState(false);
  return (
    <FadeIn delay={delay} direction="up">
      <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        style={{
          position: 'relative', background: C.white, borderRadius: 18, padding: '32px 26px',
          border: `1px solid ${hov ? C.blue + '35' : C.border}`,
          boxShadow: hov ? `0 10px 32px ${C.blue}14` : '0 2px 10px rgba(0,0,0,0.04)',
          transform: hov ? 'translateY(-4px)' : 'none',
          transition: 'all 0.22s ease', display: 'flex', flexDirection: 'column', gap: 16,
        }}>
        {/* Step number watermark */}
        <div style={{ position: 'absolute', top: 16, right: 20, fontSize: '3.5rem', fontWeight: 900, color: C.blueMd, lineHeight: 1, opacity: 0.45, letterSpacing: '-3px', userSelect: 'none' }}>{step}</div>
        <div style={{ fontSize: '2rem', lineHeight: 1 }}>{icon}</div>
        <div>
          <div style={{ fontWeight: 800, fontSize: '1rem', color: C.text, marginBottom: 8 }}>{title}</div>
          <div style={{ fontSize: '0.875rem', color: C.sub, lineHeight: 1.75 }}>{desc}</div>
        </div>
      </div>
    </FadeIn>
  );
}

/* ─── Services ───────────────────────────────────────────── */
const SERVICES = [
  {
    icon: <svg width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" /></svg>,
    title: 'Custom Synthesis Laboratory',
    text: 'From 1 mg to 1 kg — advanced equipment including bench-scale hoods, rota vapor, hydrogenator, jacketed reactors, and glass vacuum distillation units.',
    color: C.blue, bg: '#eff6ff',
    tags: ['mg–kg scale', 'Bespoke compounds'],
  },
  {
    icon: <svg width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" /></svg>,
    title: 'Research Projects',
    text: 'Drug impurity characterization, chiral resolution, synthesis of metal complexes, and isotope-labeled compounds for pharmaceutical R&D.',
    color: C.teal, bg: '#ecfeff',
    tags: ['Impurities', 'Stable isotopes'],
  },
  {
    icon: <svg width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>,
    title: 'Contract R&D Synthesis',
    text: 'Collaborative R&D delivering high-quality, cost-effective, timely synthesis solutions tailored to your specifications and regulatory requirements.',
    color: '#7c3aed', bg: '#f5f3ff',
    tags: ['CRO', 'R&D partner'],
  },
  {
    icon: <svg width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>,
    title: 'CDMO Services',
    text: 'Complex and hazardous organic chemistry — drug impurities, metabolites, secondary API standards, and stable isotope-labelled compounds at any scale.',
    color: '#059669', bg: '#f0fdf4',
    tags: ['CDMO', 'API standards'],
  },
];

function ServiceCard({ icon, title, text, color, bg, tags, delay }) {
  const [hov, setHov] = useState(false);
  return (
    <FadeIn delay={delay} direction="up">
      <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        style={{
          background: C.white, borderRadius: 18, padding: '30px 28px',
          border: `1px solid ${hov ? color + '40' : C.border}`,
          boxShadow: hov ? `0 10px 32px ${color}18` : '0 2px 8px rgba(0,0,0,0.04)',
          transform: hov ? 'translateY(-5px)' : 'none',
          transition: 'all 0.22s ease', display: 'flex', flexDirection: 'column', gap: 16,
        }}>
        <div style={{ width: 54, height: 54, borderRadius: 14, background: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</div>
        <div>
          <div style={{ fontWeight: 800, fontSize: '1rem', color: C.text, marginBottom: 8 }}>{title}</div>
          <div style={{ fontSize: '0.875rem', color: C.sub, lineHeight: 1.75 }}>{text}</div>
        </div>
        <div style={{ marginTop: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {tags.map(t => (
            <span key={t} style={{ fontSize: '0.68rem', fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: bg, color }}>{t}</span>
          ))}
        </div>
        <div>
          <Link to="/contact" style={{ fontSize: '0.8rem', color, fontWeight: 700, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            Learn More
            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
          </Link>
        </div>
      </div>
    </FadeIn>
  );
}

/* ─── Why Choose features ────────────────────────────────── */
const FEATURES = [
  { num: '01', icon: '🔬', title: 'Analytical Rigor', desc: 'Every compound characterized by HPLC, NMR, LC-MS, and IR. Purity ≥98% with lot-specific Certificate of Analysis included.' },
  { num: '02', icon: '☢️', title: 'Stable Isotopes & Impurities', desc: 'ICH Q3A/Q3B specified impurities, genotoxic impurities (ICH M7), deuterium and ¹³C-labelled stable isotope compounds.' },
  { num: '03', icon: '⚗️', title: 'Custom Synthesis', desc: 'Bespoke impurity synthesis, metabolites, chiral compounds, and isotope-labelled materials — from milligram to gram scale.' },
  { num: '04', icon: '📋', title: 'Regulatory Ready', desc: 'Standards compliant with USP, EP, BP, and ICH guidelines. Supporting FDA, EMA, CDSCO, and TGA submissions globally.' },
  { num: '05', icon: '🚀', title: 'Fast Turnaround', desc: '1–3 week standard lead time for catalog items. Rush synthesis available. Transparent communication throughout the project.' },
  { num: '06', icon: '🌐', title: 'Global Export', desc: 'Experienced in international shipment with proper documentation — import permits, SDS, CoA — to 30+ countries.' },
];

function FeatureCard({ num, icon, title, desc, delay, isBig }) {
  const [hov, setHov] = useState(false);
  return (
    <FadeIn delay={delay} direction="up">
      <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        style={{
          background: C.white, borderRadius: 16, padding: isBig ? 'clamp(24px, 4vw, 34px) clamp(20px, 4vw, 30px)' : 'clamp(20px, 3vw, 28px) clamp(18px, 3vw, 26px)',
          border: `1px solid ${hov ? C.blue + '30' : C.border}`,
          boxShadow: hov ? `0 10px 34px ${C.blue}16` : '0 2px 8px rgba(0,0,0,0.04)',
          transition: 'all 0.2s ease',
          minHeight: isBig ? 'clamp(180px, 25vh, 214px)' : 'clamp(160px, 22vh, 190px)',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <div style={{ fontSize: isBig ? 'clamp(1.4rem, 3vw, 1.8rem)' : 'clamp(1.2rem, 2.5vw, 1.6rem)', lineHeight: 1 }}>{icon}</div>
          <div style={{ fontSize: isBig ? 'clamp(1.6rem, 4vw, 2.2rem)' : 'clamp(1.4rem, 3.5vw, 1.8rem)', fontWeight: 900, color: C.blueMd, lineHeight: 1, letterSpacing: '-2px' }}>{num}</div>
        </div>
        <div style={{ fontWeight: 700, fontSize: isBig ? 'clamp(0.95rem, 2.5vw, 1.08rem)' : 'clamp(0.88rem, 2.2vw, 1rem)', color: C.text, marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: isBig ? 'clamp(0.82rem, 2vw, 0.92rem)' : 'clamp(0.78rem, 1.8vw, 0.875rem)', color: C.sub, lineHeight: 1.75 }}>{desc}</div>
      </div>
    </FadeIn>
  );
}

/* ─── Stats ──────────────────────────────────────────────── */
const STATS = [
  { icon: '🧬', value: 500, suffix: '+', label: 'Catalog Items', fromApi: true },
  { icon: '✅', value: 98, prefix: '≥', suffix: '%', label: 'Purity Guaranteed' },
  { icon: '🌍', value: 30, suffix: '+', label: 'Countries Served' },
  { icon: '⚡', value: 2, suffix: ' weeks', label: 'Max Lead Time' },
  { icon: '🏭', value: 2021, suffix: '', label: 'Founded' },
];

/* ─── Client logos ───────────────────────────────────────── */
const CLIENT_LOGOS = [
  { src: '/clients/ajanta-pha-logo.svg', alt: 'Ajanta Pharma', h: 38 },
  { src: '/clients/alembic-Logo.gif', alt: 'Alembic', h: 38 },
  { src: '/clients/amneal-logo.svg', alt: 'Amneal', h: 38 },
  { src: '/clients/biocon-logo-vector.svg', alt: 'Biocon', h: 38 },
  { src: '/clients/Biosys.png', alt: 'Biosys', h: 46 },
  { src: '/clients/Cliantha_logo.png', alt: 'Cliantha', h: 38 },
  { src: '/clients/macleods-logo.jpeg', alt: 'Macleods', h: 38 },
  { src: '/clients/PI-Health-Sciences-Logo.svg', alt: 'PI Health Sciences', h: 38 },
  { src: '/clients/syngene.jpeg', alt: 'Syngene', h: 38 },
  { src: '/clients/aurigene.jpg', alt: 'Aurigene', h: 38 },
];

/* ─── Testimonials ───────────────────────────────────────── */
const TESTIMONIALS = [
  {
    quote: "PRASI LABS delivered a custom impurity standard with ≥99% purity and full NMR/HPLC data in under two weeks. Outstanding turnaround for our regulatory submission.",
    name: "Dr. Suresh Kumar",
    role: "Analytical R&D Lead",
    company: "Leading Pharma CRO, Hyderabad",
    initials: "SK",
    color: '#1d4ed8',
  },
  {
    quote: "We've sourced ICH Q3 impurities from multiple vendors. PRASI LABS consistently delivers the best purity, documentation, and pricing. Our go-to partner for impurity standards.",
    name: "Priya Nair",
    role: "QA Manager",
    company: "Multinational Pharma, Bengaluru",
    initials: "PN",
    color: '#0891b2',
  },
  {
    quote: "The stable isotope-labelled compounds we ordered were delivered with complete spectral data. Their custom synthesis capabilities are genuinely impressive.",
    name: "Dr. Amit Joshi",
    role: "Senior Scientist",
    company: "Bioanalytical Lab, Pune",
    initials: "AJ",
    color: '#059669',
  },
  {
    quote: "Fast response, competitive pricing, and excellent analytical documentation. PRASI LABS has made our method development process significantly smoother.",
    name: "Kavitha Rao",
    role: "Head of Regulatory Affairs",
    company: "Generics Manufacturer, Mumbai",
    initials: "KR",
    color: '#7c3aed',
  },
];

/* ─── Accreditations ─────────────────────────────────────── */
const ACCREDITATIONS = [
  { badge: 'ICH', sub: 'Q1–Q14 Compliant', color: C.blue },
  { badge: 'USP', sub: 'Reference Standards', color: '#7c3aed' },
  { badge: 'EP', sub: 'European Pharmacopoeia', color: C.teal },
  { badge: 'BP', sub: 'British Pharmacopoeia', color: '#059669' },
  { badge: 'ISO', sub: '9001 Certified', color: '#d97706' },
];

/* ══════════════════════════════════════════════════════════ */
export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [total, setTotal] = useState(0);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [heroSearch, setHeroSearch] = useState('');
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    setTimeout(() => setHeroLoaded(true), 80);
    api.get('/public/catalog', { params: { limit: 6 } })
      .then(r => { setFeatured(r.data.data || []); setTotal(r.data.total || 0); })
      .catch(() => { });
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const t = setInterval(() => setActiveTestimonial(p => (p + 1) % TESTIMONIALS.length), 5000);
    return () => clearInterval(t);
  }, []);

  const handleHeroSearch = (e) => {
    e.preventDefault();
    if (heroSearch.trim()) navigate(`/catalog?search=${encodeURIComponent(heroSearch.trim())}`);
    else navigate('/catalog');
  };

  const homeLd = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': 'https://www.prasilabs.com/#localbusiness',
    name: 'PRASI LABS Pvt Limited',
    image: 'https://www.prasilabs.com/prasilabs-logo.png',
    url: 'https://www.prasilabs.com',
    telephone: '+91-9160006515',
    email: 'info@prasilabs.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Plot No 23 & 24, Shaktipuram, TSIIC Extn, Prashanthi Nagar, IDA Kukatpally',
      addressLocality: 'Hyderabad',
      addressRegion: 'Telangana',
      postalCode: '500072',
      addressCountry: 'IN',
    },
    geo: { '@type': 'GeoCoordinates', latitude: 17.4849, longitude: 78.3901 },
    openingHours: 'Mo-Sa 09:30-18:00',
    priceRange: '$$',
    sameAs: ['https://www.linkedin.com/company/prasi-labs'],
  };

  return (
    <>
      <SEO
        canonical="/"
        description="PRASI LABS — Leading supplier of certified pharmaceutical reference standards, drug impurity standards, metabolites, stable isotopes, and custom synthesis services. USP, EP, BP pharmacopeia grades. Based in Hyderabad, India."
        keywords="pharmaceutical reference standards India, drug impurity standards supplier, custom synthesis CRO, stable isotopes, pharma impurities Hyderabad, PRASI LABS, reference standards USP EP BP"
        jsonLd={homeLd}
      />
      <div style={{ background: C.bg }}>
        <style>{`
        @keyframes ticker-scroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .clients-ticker { display: flex; align-items: center; gap: 72px; animation: ticker-scroll 30s linear infinite; width: max-content; }
        .clients-ticker:hover { animation-play-state: paused; }
        @keyframes hero-float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-12px); } }
        @keyframes pulse-ring { 0% { transform: scale(1); opacity: 0.7; } 100% { transform: scale(1.4); opacity: 0; } }
        @keyframes hero-bg-shift { 0%, 100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
      `}</style>

        {/* ── Hero ─────────────────────────────────────────── */}
        <section style={{ position: 'relative', minHeight: '72vh', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', color: '#fff' }}>
          <video autoPlay muted loop playsInline
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}>
            <source src="/carousel-1.mp4" type="video/mp4" />
          </video>
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(10,22,40,0.88) 0%, rgba(29,78,216,0.6) 100%)', zIndex: 1 }} />
          {/* Orbiting atom — left decoration */}
          <OrbitingAtom size={220} color="white" style={{ position: 'absolute', left: '6%', top: '50%', transform: 'translateY(-50%)', opacity: 0.09, pointerEvents: 'none', zIndex: 2 }} />

          {/* Animated molecule decoration */}
          <svg style={{ position: 'absolute', right: '8%', top: '50%', transform: 'translateY(-50%)', opacity: 0.08, animation: 'hero-float 6s ease-in-out infinite' }} width="300" height="260" viewBox="0 0 300 260" zIndex={2}>
            <circle cx="75" cy="130" r="36" stroke="white" strokeWidth="2.5" fill="none" />
            <circle cx="150" cy="55" r="36" stroke="white" strokeWidth="2.5" fill="none" />
            <circle cx="225" cy="130" r="36" stroke="white" strokeWidth="2.5" fill="none" />
            <circle cx="150" cy="205" r="36" stroke="white" strokeWidth="2.5" fill="none" />
            <line x1="108" y1="107" x2="126" y2="83" stroke="white" strokeWidth="2.5" />
            <line x1="174" y1="83" x2="192" y2="107" stroke="white" strokeWidth="2.5" />
            <line x1="192" y1="153" x2="174" y2="177" stroke="white" strokeWidth="2.5" />
            <line x1="126" y1="177" x2="108" y2="153" stroke="white" strokeWidth="2.5" />
            <circle cx="75" cy="130" r="8" fill="white" opacity="0.6" />
            <circle cx="225" cy="130" r="8" fill="white" opacity="0.6" />
          </svg>
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 120, background: `linear-gradient(to bottom, transparent, ${C.bg})`, zIndex: 3 }} />

          <div className="pub-hero-inner" style={{
            position: 'relative', zIndex: 4, maxWidth: 860, margin: '0 auto',
            textAlign: 'center',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
            opacity: heroLoaded ? 1 : 0, transform: heroLoaded ? 'translateY(0)' : 'translateY(30px)',
          }}>
            {/* Live badge */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.1)', borderRadius: 24, padding: '6px 18px', fontSize: '0.72rem', fontWeight: 700, marginBottom: 32, border: '1px solid rgba(255,255,255,0.18)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              <span style={{ position: 'relative', display: 'inline-block' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#34d399', display: 'inline-block' }} />
                <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '1px solid #34d399', animation: 'pulse-ring 1.6s ease-out infinite' }} />
              </span>
              Drug Impurities · Stable Isotopes · Custom Synthesis — Hyderabad, India
            </div>

            <h1 style={{ fontSize: 'clamp(2.2rem, 5.5vw, 3.8rem)', fontWeight: 900, lineHeight: 1.08, marginBottom: 26, letterSpacing: '-1.5px' }}>
              Specialized in Custom Synthesis<br />
              <span style={{ background: 'linear-gradient(90deg, #60a5fa, #34d399)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                and Rare Impurities
              </span>
            </h1>

            <p style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.82)', maxWidth: 620, margin: '0 auto 44px', lineHeight: 1.8 }}>
              PRASI LABS supplies pharmaceutical drug impurity standards, stable isotope-labelled compounds, reference standards, and custom synthesis — purity backed by HPLC, NMR, and MS data.
            </p>

            {/* Trust pills */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 36 }}>
              {['ICH Compliant', 'USP / EP / BP', '≥98% Purity', 'CoA Included'].map(t => (
                <div key={t} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 20, padding: '4px 14px', fontSize: '0.72rem', fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>{t}</div>
              ))}
            </div>

            {/* Hero search bar */}
            <form onSubmit={handleHeroSearch} className="pub-hero-search" style={{ background: 'rgba(255,255,255,0.95)', borderRadius: 12, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.28)', border: '1px solid rgba(255,255,255,0.3)' }}>
              <div className="pub-search-icon" style={{ display: 'flex', alignItems: 'center', paddingLeft: 16, color: '#94a3b8', flexShrink: 0 }}>
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
              </div>
              <input
                value={heroSearch}
                onChange={e => setHeroSearch(e.target.value)}
                placeholder="Search by compound name, CAS number, or catalog ID…"
                style={{ flex: 1, padding: '14px 12px', border: 'none', outline: 'none', fontSize: '0.88rem', color: '#0f172a', background: 'transparent', minWidth: 0 }}
              />
              <button type="submit"
                style={{ padding: '10px 22px', background: C.blue, color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem', borderRadius: '0 11px 11px 0', flexShrink: 0, transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = C.blueHov}
                onMouseLeave={e => e.currentTarget.style.background = C.blue}>
                Search
              </button>
            </form>

            <div className="pub-hero-btns">
              <Link to="/catalog" style={{ padding: '14px 36px', background: '#fff', color: C.blue, borderRadius: 10, fontWeight: 800, textDecoration: 'none', fontSize: '0.95rem', boxShadow: '0 4px 22px rgba(0,0,0,0.22)', letterSpacing: '0.01em', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                Browse Catalog
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
              </Link>
              <Link to="/contact" style={{ padding: '14px 36px', background: 'rgba(255,255,255,0.12)', color: '#fff', borderRadius: 10, fontWeight: 700, textDecoration: 'none', fontSize: '0.95rem', border: '1.5px solid rgba(255,255,255,0.3)', textAlign: 'center' }}>
                Request a Quote
              </Link>
            </div>
          </div>
        </section>

        {/* ── Stats strip ──────────────────────────────────── */}
        <section style={{ background: C.white, borderBottom: `1px solid ${C.border}`, marginTop: -2 }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px', display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}>
            {STATS.map((s, i, arr) => (
              <React.Fragment key={s.label}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '26px 24px' }}>
                  <div style={{ width: 46, height: 46, borderRadius: 12, background: C.blueLt, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '1.2rem' }}>
                    {s.icon}
                  </div>
                  <div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 900, color: C.blue, lineHeight: 1.1, letterSpacing: '-0.5px' }}>
                      {s.fromApi && total > 0
                        ? <CountUp target={total} suffix="+" />
                        : <CountUp target={s.value} prefix={s.prefix || ''} suffix={s.suffix || ''} />
                      }
                    </div>
                    <div style={{ fontSize: '0.78rem', color: C.sub, marginTop: 2 }}>{s.label}</div>
                  </div>
                </div>
                {i < arr.length - 1 && <div className="pub-stats-divider" style={{ width: 1, background: C.border, margin: '16px 0' }} />}
              </React.Fragment>
            ))}
          </div>
        </section>

        {/* ── Featured Products ─────────────────────────────── */}
        {featured.length > 0 && (
          <section className="pub-section">
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
              <FadeIn>
                <SectionHeading
                  label="Featured Products"
                  title="Popular Pharmaceutical Standards"
                  subtitle="A curated selection from our comprehensive catalog of drug impurities and reference materials."
                />
              </FadeIn>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24, marginBottom: 44 }}>
                {featured.map((item, i) => <ProductCard key={item.id} item={item} delay={i * 70} />)}
              </div>
              <FadeIn delay={200}>
                <div style={{ textAlign: 'center' }}>
                  <Link to="/catalog" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 36px', background: C.blue, color: '#fff', borderRadius: 10, fontWeight: 700, textDecoration: 'none', fontSize: '0.9rem', boxShadow: `0 4px 20px ${C.blue}44` }}>
                    View Full Catalog ({total > 0 ? `${total}+ items` : 'all items'})
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                  </Link>
                </div>
              </FadeIn>
            </div>
          </section>
        )}

        {/* ── How It Works ─────────────────────────────────── */}
        <section className="pub-section" style={{ background: '#f0f4ff', position: 'relative', overflow: 'hidden' }}>
          <BubblingFlask size={180} color="#1d4ed8" style={{ position: 'absolute', right: 24, bottom: 16, opacity: 0.12, pointerEvents: 'none' }} />
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <FadeIn>
              <SectionHeading
                label="How It Works"
                title="From Inquiry to Delivery"
                subtitle="A streamlined process designed for pharmaceutical scientists and procurement teams."
              />
            </FadeIn>
            {/* Connector line */}
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: 36, left: '12%', right: '12%', height: 2, background: `linear-gradient(90deg, ${C.blue}22, ${C.blue}55, ${C.blue}22)`, zIndex: 0, display: 'none' }} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 24 }}>
                {PROCESS.map((p, i) => <ProcessStep key={p.step} {...p} delay={i * 100} />)}
              </div>
            </div>
          </div>
        </section>

        {/* ── Our Services ─────────────────────────────────── */}
        <section className="pub-section">
          <div style={{ maxWidth: 1200, margin: '0 auto' }}>
            <FadeIn>
              <SectionHeading
                label="Our Services"
                title="Empowering Research & Development"
                subtitle="Comprehensive pharmaceutical chemistry services tailored to your exact requirements."
              />
            </FadeIn>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
              {SERVICES.map((s, i) => <ServiceCard key={s.title} {...s} delay={i * 80} />)}
            </div>
          </div>
        </section>

        {/* ── Why Choose ───────────────────────────────────── */}
        <section className="pub-section">
          <div style={{ maxWidth: 1200, margin: '0 auto' }}>
            <FadeIn>
              <SectionHeading
                label="Why PRASI LABS"
                title="Quality You Can Count On"
                subtitle="India-based supplier trusted by pharma companies and CROs across the globe — from Hyderabad to the world."
              />
            </FadeIn>
            <div className="pub-quality-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(260px, 1fr))', gap: 24 }}>
              {FEATURES.map((f, i) => (
                <FeatureCard key={f.num} {...f} delay={i * 70} isBig={i >= 2} />
              ))}
            </div>
            <style>{`
            .pub-quality-grid { 
              grid-template-columns: repeat(3, minmax(260px, 1fr)); 
              padding: 0 24px;
            }
            @media (max-width: 1100px) { 
              .pub-quality-grid { 
                grid-template-columns: repeat(2, minmax(280px, 1fr));
                gap: 20px;
              }
            }
            @media (max-width: 768px) { 
              .pub-quality-grid { 
                grid-template-columns: 1fr;
                gap: 16px;
                padding: 0 20px;
              }
            }
            @media (max-width: 480px) { 
              .pub-quality-grid { 
                grid-template-columns: 1fr;
                gap: 14px;
                padding: 0 16px;
              }
            }
          `}</style>
          </div>
        </section>

        {/* ── Our Clients ──────────────────────────────────── */}
        <section style={{ background: C.white, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, padding: 'clamp(40px,6vw,64px) 0', overflow: 'hidden' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 40, padding: '0 24px' }}>
              <SectionLabel>Trusted Industry Partnership</SectionLabel>
              <h2 style={{ fontSize: '1.7rem', fontWeight: 900, color: C.text, marginBottom: 8, letterSpacing: '-0.4px' }}>Trusted by Leading Pharma Companies</h2>
              <p style={{ fontSize: '0.9rem', color: C.sub }}>Clients across India, Europe, the US, and Asia-Pacific</p>
            </div>
          </FadeIn>
          <div style={{ overflow: 'hidden', width: '100%' }}>
            <div className="clients-ticker">
              {[...CLIENT_LOGOS, ...CLIENT_LOGOS].map((logo, i) => (
                <div key={i} style={{ flexShrink: 0, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img src={logo.src} alt={logo.alt}
                    style={{ height: logo.h, width: 'auto', maxWidth: 160, objectFit: 'contain', filter: 'grayscale(30%)', opacity: 0.78, transition: 'all 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.filter = 'grayscale(0%)'; e.currentTarget.style.opacity = '1'; }}
                    onMouseLeave={e => { e.currentTarget.style.filter = 'grayscale(30%)'; e.currentTarget.style.opacity = '0.78'; }}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Accreditations ───────────────────────────────── */}
        <section className="pub-section-sm" style={{ background: C.white, borderTop: `1px solid ${C.border}` }}>
          <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <FadeIn>
              <div style={{ textAlign: 'center', marginBottom: 36 }}>
                <SectionLabel>Compliance & Standards</SectionLabel>
                <h3 style={{ fontWeight: 800, fontSize: '1.4rem', color: C.text }}>Regulatory Compliance</h3>
              </div>
            </FadeIn>
            <div style={{ display: 'flex', gap: 20, justifyContent: 'center', flexWrap: 'wrap' }}>
              {ACCREDITATIONS.map((a, i) => (
                <FadeIn key={a.badge} delay={i * 80} direction="up">
                  <div style={{ textAlign: 'center', padding: '20px 28px', background: C.bg, border: `2px solid ${a.color}20`, borderRadius: 16, minWidth: 100 }}>
                    <div style={{ width: 52, height: 52, borderRadius: 14, background: a.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '0.88rem', margin: '0 auto 10px' }}>{a.badge}</div>
                    <div style={{ fontSize: '0.73rem', color: C.sub, fontWeight: 600 }}>{a.sub}</div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* ── Testimonials ─────────────────────────────────── */}
        <section className="pub-section" style={{ background: C.bg }}>
          <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <FadeIn>
              <SectionHeading
                label="Client Stories"
                title="Trusted by Pharma Scientists"
                subtitle="What researchers and procurement teams say about working with PRASI LABS."
              />
            </FadeIn>

            {/* Active testimonial */}
            <FadeIn delay={100}>
              <div className="pub-testimonial-inner" style={{ background: C.white, borderRadius: 20, border: `1px solid ${C.border}`, boxShadow: '0 8px 32px rgba(0,0,0,0.07)', marginBottom: 28, position: 'relative', overflow: 'hidden', minHeight: 200 }}>
                {/* Big quote mark */}
                <div style={{ position: 'absolute', top: 16, left: 28, fontSize: '7rem', lineHeight: 1, color: TESTIMONIALS[activeTestimonial].color, opacity: 0.08, fontFamily: 'Georgia, serif', userSelect: 'none', pointerEvents: 'none' }}>"</div>
                <blockquote style={{ fontSize: '1.08rem', color: C.text, lineHeight: 1.85, fontStyle: 'italic', marginBottom: 28, position: 'relative', zIndex: 1, margin: '0 0 28px' }}>
                  "{TESTIMONIALS[activeTestimonial].quote}"
                </blockquote>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 46, height: 46, borderRadius: '50%', background: TESTIMONIALS[activeTestimonial].color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.9rem', flexShrink: 0 }}>
                    {TESTIMONIALS[activeTestimonial].initials}
                  </div>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: '0.95rem', color: C.text }}>{TESTIMONIALS[activeTestimonial].name}</div>
                    <div style={{ fontSize: '0.8rem', color: C.sub }}>{TESTIMONIALS[activeTestimonial].role} · {TESTIMONIALS[activeTestimonial].company}</div>
                  </div>
                  {/* Star rating */}
                  <div className="pub-testimonial-stars" style={{ marginLeft: 'auto', display: 'flex', gap: 3 }}>
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} width="16" height="16" fill="#f59e0b" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
                    ))}
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* Dot navigation */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              {TESTIMONIALS.map((_, i) => (
                <button key={i} onClick={() => setActiveTestimonial(i)}
                  style={{ width: i === activeTestimonial ? 28 : 8, height: 8, borderRadius: 4, border: 'none', cursor: 'pointer', background: i === activeTestimonial ? C.blue : C.border, transition: 'all 0.3s ease', padding: 0 }} />
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA Banner ───────────────────────────────────── */}
        <section className="pub-section-dark" style={{ background: `linear-gradient(135deg, ${C.navy} 0%, #1e3a8a 50%, ${C.blue} 100%)`, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
          <svg style={{ position: 'absolute', right: 60, top: '50%', transform: 'translateY(-50%)', opacity: 0.05 }} width="320" height="280" viewBox="0 0 320 280">
            <circle cx="80" cy="140" r="38" stroke="white" strokeWidth="3" fill="none" />
            <circle cx="160" cy="60" r="38" stroke="white" strokeWidth="3" fill="none" />
            <circle cx="240" cy="140" r="38" stroke="white" strokeWidth="3" fill="none" />
            <circle cx="160" cy="220" r="38" stroke="white" strokeWidth="3" fill="none" />
            <line x1="115" y1="118" x2="132" y2="96" stroke="white" strokeWidth="3" />
            <line x1="188" y1="96" x2="205" y2="118" stroke="white" strokeWidth="3" />
            <line x1="205" y1="162" x2="188" y2="184" stroke="white" strokeWidth="3" />
            <line x1="132" y1="184" x2="115" y2="162" stroke="white" strokeWidth="3" />
          </svg>
          <FadeIn>
            <div style={{ maxWidth: 680, margin: '0 auto', position: 'relative', zIndex: 1 }}>
              <SectionLabel light>Custom Synthesis</SectionLabel>
              <h2 style={{ color: '#fff', fontSize: 'clamp(1.8rem, 3.5vw, 2.6rem)', fontWeight: 900, marginBottom: 18, letterSpacing: '-0.5px', lineHeight: 1.15 }}>
                Need a Bespoke Compound?
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.78)', fontSize: '1.05rem', marginBottom: 40, lineHeight: 1.8 }}>
                Our synthesis team in Hyderabad prepares drug impurities, stable isotope-labelled compounds, and metabolites to your exact specifications — with complete analytical data and regulatory-grade certification.
              </p>
              <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link to="/contact" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '15px 38px', background: '#fff', color: C.blue, borderRadius: 10, fontWeight: 800, textDecoration: 'none', fontSize: '1rem', boxShadow: '0 6px 24px rgba(0,0,0,0.24)' }}>
                  Contact Our Scientists
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                </Link>
                <Link to="/catalog" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '15px 38px', background: 'rgba(255,255,255,0.12)', color: '#fff', borderRadius: 10, fontWeight: 700, textDecoration: 'none', fontSize: '1rem', border: '1.5px solid rgba(255,255,255,0.3)' }}>
                  Browse Catalog
                </Link>
              </div>
            </div>
          </FadeIn>
        </section>

      </div>
    </>
  );
}
